/* eslint-disable no-param-reassign */

import _ from 'lodash';
import jwtDecode from 'jwt-decode';
import { delay } from 'redux-saga/effects'
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as C from './userConstants';
import * as SC from '../sharedConstants';
import { gatekeeper } from './index';

import { providers } from './usersReducers';

const GRANT_RE = /^(.*):([\w]+)$/;

const GRANTS = (process.env.GRANTS || '')
  .split(',')
  .reduce((grants, term) => {
    if (GRANT_RE.test(term)) {
      const m = GRANT_RE.exec(term);
      const regexTestString = m[1];
      const grant = m[2];
      if (regexTestString && grant) {
        try {
          const re = new RegExp(regexTestString, 'i');
          grants.push({
            regexTestString,
            test: (str) => {
              try {
                if (str && typeof str === 'string') {
                  return re.test(str);
                }
                console.log('bad test subject: ', str, re);
                return false;
              } catch (err) {
                console.log('error testing string: ', str, err);
                return false;
              }
            },
            grant,
          });
        } catch (err) {
          console.log('error in regexp ', regexTestString, err.message);
        }
      }
    }
    return grants;
  }, []);

export default (bottle) => {
  bottle.factory('watchSession', ({}) => function* watchSession(action) {
    const accessToken = _.get(action, 'payload.accessToken');

    if (accessToken) {
      yield put(providers.setAccessToken(accessToken));
    } else {
      console.log('no access token in ', action);
    }
    const idToken = _.get(action, 'payload.idToken');
    if (idToken) {
      yield put(providers.setIDToken(idToken));
    } else {
      console.log('no id token in ', action);
    }
    yield put(providers.checkExpiration());
  });

  bottle.constant('WATCH_EXP_TIME', 5 * 1000);

  bottle.factory('watchExpiration', ({ WATCH_EXP_TIME }) => function* () {
    // @TODO: ensure single thread
    // eslint-disable-next-line prefer-const
    let [expiresIn, lastSavedIDToken] = yield select(state => [
      _.get(state, 'users.session.expiresIn'),
      _.get(state, 'users.idToken'),
    ]);

    if (lastSavedIDToken) {
      try {
        const tokenData = jwtDecode(lastSavedIDToken);
        const exp = _.get(tokenData, 'exp');
        if (exp && _.isNumber(exp)) {
          expiresIn = 1000 * exp;
        }
      } catch (err) {
        console.log('watchExpiration error -- ', err);
      }
    }

    if (expiresIn) {
      if (expiresIn < Date.now()) {
        yield put(providers.userLogout());
      } else {
        yield delay( expiresIn - Date.now());
        yield put(providers.checkExpiration());
      }
    } else {
      try {
        yield delay(WATCH_EXP_TIME);
        yield put(providers.checkExpiration());
      } catch (err) {
        console.log('watchExpiration failure', err);
      }
    }
  });

  bottle.factory('ephemeralKeyFromIDToken', ({ getEphemeralToken }) => function* ({ payload: IDToken }) {
    // if the parameter doesn't include the ID token get it from state.
    if (!IDToken) {
      const [lastSavedIDToken] = yield select(state => [
        _.get(state, 'users.idToken'),
      ]);

      IDToken = lastSavedIDToken;
    }

    // if STATE doesn't have the ID token stop.
    if (!IDToken) {
      yield put(providers.setEphemeralKey(null));
      return;
    }

    let key;
    yield put(providers.setTokenStatus(SC.LOADING));
    try {
      key = yield call(getEphemeralToken, IDToken);
    } catch (err) {
      console.log('cannot get ephemeral token:', err);
      yield put(providers.setEphemeralKey(null));
      yield put(providers.userLogout());
      return;
    }

    yield put(providers.setEphemeralKey(key));

    const exp = _.get(key, 'exp');
    if (!(exp && _.isNumber(exp))) {
      return;
    }

    const refreshTime = exp - Date.now() - (10 * 1000);
    // refresh the token ten seconds before time.
    if (refreshTime > 0) {
      yield call(delay, refreshTime);

      const [lastSavedExp, lastSavedIDToken] = yield select(state => [
        _.get(state, 'users.ephemeralKey.exp'),
        _.get(state, 'users.idToken'),
      ]);

      if (lastSavedExp === exp && lastSavedIDToken === IDToken) {
        // the ID and bearer token we loaded is still being used
        yield put(providers.reloadEphemeralKey());
      }
    }
  });

  bottle.factory('watchEphemeral', ({ ephemeralKeyFromIDToken }) => function* (data) {
    const HALF_HOUR = 60 * 60 * 0.5;

    const token = data.payload;
    if (token) {
      const { expirationDate } = token;
      if (expirationDate) {
        const when = Date.parse(expirationDate);
        let delayTime = HALF_HOUR;
        try {
          const now = Date.now();
          delayTime = Math.max(HALF_HOUR, when - now);
        } catch (err) {
          console.log('error: ', err);
        }
        yield delay(delayTime);
      } else {
        yield delay(HALF_HOUR);
      }
      yield put(providers.reloadEphemeralKey());
    } else {
      console.log('ephemeral - no payload in ', data);
      yield delay(HALF_HOUR);
    }
  });

  bottle.factory('bearerTokenStatus', () => function* ({ payload: key }) {
    const token = _.get(key, 'bearerToken');
    if (token) {
      yield put(providers.setTokenStatus(SC.LOADED));
    } else {
      yield put(providers.setTokenStatus(SC.NOT_LOADED));
    }
  });


  bottle.factory('profileToGrants', () => function* ({ payload: profile }) {
    const email = _.get(profile, 'email');
    const userGrants = [];
    if (email) {
      GRANTS.forEach(({ grant, regexTestString, test }) => {
        if (test(email)) {
          console.log('... passes test ', regexTestString, 'IS adding grant ', grant);
          userGrants.push(grant);
        } else {
          console.log('... FAILS test ', regexTestString, 'NOT adding grant', grant);
        }
      });
    } else {
      console.log('---- profileToGrants -- no email present');
    }
    yield put(providers.setGrants(userGrants));
    const [pages] = yield select(state => [
      _.get(state, 'modelPages.pages'),
    ]);

    const userPages = pages.filter((page) => {
      const l = _.get(page, 'access.length', 0);
      for (let i = 0; i < l; i += 1) {
        const access = page.access[i];
        if (!userGrants.includes(access)) {
          return false;
        }
      }
      return true;
    });

    yield put(providers.setUserPages(userPages));
  });

  bottle.factory('watchEphemeralKey', ({ bearerTokenStatus }) => function* watchEphemeralKey() {
    yield takeEvery(
      C.A_SET_EPHEMERAL_KEY,
      bearerTokenStatus,
    );
  });

  bottle.factory('watchAccessTokenSaga', ({ ephemeralKeyFromIDToken }) => function* watchAccessTokenSaga() {
    yield takeLatest(
      C.A_SET_ID_TOKEN,
      ephemeralKeyFromIDToken,
    );
  });

  bottle.factory('profileToGrantsSaga', ({ profileToGrants }) => function* profileToGrantsSaga() {
    yield takeEvery(C.A_SET_PROFILE, profileToGrants);
  });

  bottle.factory('watchSessionSaga', ({ watchSession }) => function* watchSessionSaga() {
    yield takeEvery(
      C.A_SET_SESSION,
      watchSession,
    );
  });

  bottle.factory('watchReloadKey', ({ ephemeralKeyFromIDToken }) => function* watchSessionSaga() {
    yield takeEvery(
      C.A_RELOAD_EPHEMERAL_KEY,
      ephemeralKeyFromIDToken,
    );
  });

  bottle.factory('checkExpiration', ({ watchExpiration }) => function* checkExpirationSaga() {
    yield takeEvery(C.A_CHECK_EXPIRATION, watchExpiration);
  });

  bottle.factory('autoReloadEphemeral', ({ watchEphemeral }) => function* onEphemeral() {
    yield takeEvery(C.A_SET_EPHEMERAL_KEY, watchEphemeral);
  });
};
